import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Briefcase,
  Users,
  Wallet,
  IndianRupee,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Activity,
  Building2,
  TrendingUp,
  FileText,
  ChevronRight,
  AlertCircle,
  Inbox,
  ArrowRight,
  Repeat,
  Plus,
} from 'lucide-react';
import { formatMoney, isAdmin, hasPermission } from '@vns/core';
import { useSession } from '../lib/session';
import { useDashboard, useStaffDashboard } from '../lib/queries';
import { useRecentActivity, exactWhen, timeAgo } from '../lib/activity';
import Shell from '../components/Shell';
import StockTransferDrawer from '../components/StockTransferDrawer';
import { Icon, StatusChip, EmptyState, TableSkeleton, ErrorState, KpiSkeleton } from '../components/ui';

export default function Dashboard() {
  const actor = useSession((s) => s.actor);
  return isAdmin(actor) ? <AdminDashboard /> : <StaffDashboard />;
}

/* ------------------------------- Admin Dashboard ------------------------------- */

function AdminDashboard() {
  const navigate = useNavigate();
  const actor = useSession((s) => s.actor);
  const { data, isLoading, error, refetch } = useDashboard();
  const totalPending = data?.pending.reduce((sum, p) => sum + p.balancePaise, 0) ?? 0;
  const firstName = actor?.fullName?.split(' ')[0] ?? 'Executive';

  const todayStr = new Date().toLocaleDateString('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return (
    <Shell
      title="Dashboard"
      crumb="Overview"
      actions={
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Link to="/reports" className="btn btn-auto btn-sm btn-ghost" style={{ display: 'flex', alignItems: 'center', gap: 5, textDecoration: 'none', whiteSpace: 'nowrap' }}>
            <FileText size={14} />
            <span>Reports</span>
          </Link>
          <Link to="/projects" className="btn btn-primary btn-auto btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 5, textDecoration: 'none', whiteSpace: 'nowrap' }}>
            <Briefcase size={14} />
            <span>Projects</span>
          </Link>
        </div>
      }
    >
      {/* Welcome Banner */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          padding: '16px 20px',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          marginBottom: 20,
          boxShadow: 'var(--shadow)',
        }}
      >
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
            Welcome back, {firstName} 👋
          </h2>
          <div style={{ fontSize: 12.5, color: 'var(--text-faint)', marginTop: 3 }}>
            Here is your live construction and financial overview for today.
          </div>
        </div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            fontFamily: 'var(--font-secondary)',
            color: 'var(--text-soft)',
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            padding: '6px 12px',
            borderRadius: 'var(--radius-sm)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Clock size={13} style={{ color: 'var(--accent)' }} />
          <span>{todayStr}</span>
        </div>
      </motion.div>

      {/* KPI Stat Cards */}
      {isLoading ? (
        <KpiSkeleton count={4} />
      ) : (
        <motion.div
          className="kpi-row"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35, staggerChildren: 0.08 }}
        >
          {/* Active Projects */}
          <motion.div
            className="kpi"
            whileHover={{ y: -2 }}
            transition={{ duration: 0.15 }}
            onClick={() => navigate('/projects')}
            style={{ cursor: 'pointer' }}
          >
            <div className="lbl">
              <span>Active Projects</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Briefcase size={15} strokeWidth={2} />
              </div>
            </div>
            <div className="val">{data?.activeProjects ?? 0}</div>
            <div className="sub">
              <span>of {data?.totalProjects ?? 0} total sites registered</span>
            </div>
          </motion.div>

          {/* Active Clients */}
          <motion.div
            className="kpi"
            whileHover={{ y: -2 }}
            transition={{ duration: 0.15 }}
            onClick={() => navigate('/clients')}
            style={{ cursor: 'pointer' }}
          >
            <div className="lbl">
              <span>Client Accounts</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#f5f3ff', color: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Users size={15} strokeWidth={2} />
              </div>
            </div>
            <div className="val">{data?.activeClients ?? 0}</div>
            <div className="sub">active developer & owner profiles</div>
          </motion.div>

          {/* Outstanding Accounts */}
          <motion.div
            className="kpi"
            whileHover={{ y: -2 }}
            transition={{ duration: 0.15 }}
          >
            <div className="lbl">
              <span>Awaiting Payment</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AlertCircle size={15} strokeWidth={2} />
              </div>
            </div>
            <div className="val">{data?.pending.length ?? 0}</div>
            <div className="sub">projects with pending collections</div>
          </motion.div>

          {/* Total Outstanding Amount */}
          <motion.div
            className="kpi"
            whileHover={{ y: -2 }}
            transition={{ duration: 0.15 }}
          >
            <div className="lbl">
              <span>Total Outstanding</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IndianRupee size={15} strokeWidth={2} />
              </div>
            </div>
            <div className="val" style={{ color: '#ea580c' }}>
              {formatMoney(totalPending)}
            </div>
            <div className="sub">unpaid receivables balance</div>
          </motion.div>
        </motion.div>
      )}

      {/* Pending Client Payments Table */}
      <motion.div
        className="card"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.1 }}
      >
        <div className="card-head">
          <div className="card-head-title-group">
            <div className="card-head-icon-box card-head-icon-blue">
              <Wallet size={20} strokeWidth={2.2} />
            </div>
            <div className="card-head-text">
              <h3>Pending Client Payments</h3>
              <div className="desc">Live projects with budget, received collections, and outstanding balance</div>
            </div>
          </div>
          <Link to="/projects" className="btn btn-auto btn-sm btn-ghost card-head-desktop-link" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', flexShrink: 0 }}>
            <span>View all projects</span>
            <ChevronRight size={13} />
          </Link>
        </div>

        <div className="table-wrap">
          {isLoading ? (
            <TableSkeleton rows={4} cols={6} />
          ) : error ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : !data?.pending.length ? (
            <EmptyState
              icon="check"
              title="Nothing outstanding"
              hint="All live projects are currently fully paid up."
            />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Project Name</th>
                  <th>Client</th>
                  <th>Project Budget</th>
                  <th>Received Collections</th>
                  <th>Pending Amount</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.pending.map((p) => (
                  <tr
                    key={p.projectId}
                    className="clickable"
                    onClick={() => navigate(`/projects/${p.projectId}`)}
                  >
                    <td className="name">
                      <div style={{ fontWeight: 600, color: 'var(--text)' }}>{p.projectName}</div>
                    </td>
                    <td>
                      <span style={{ color: 'var(--text-soft)', fontWeight: 500 }}>{p.clientName}</span>
                    </td>
                    <td className="num" style={{ fontWeight: 500 }}>
                      {formatMoney(p.valuePaise)}
                    </td>
                    <td className="num" style={{ color: '#059669', fontWeight: 600 }}>
                      {formatMoney(p.paidPaise)}
                    </td>
                    <td className="num" style={{ color: '#ea580c', fontWeight: 700 }}>
                      {formatMoney(p.balancePaise)}
                    </td>
                    <td>
                      <span
                        className="btn btn-auto btn-sm btn-ghost"
                        style={{
                          fontSize: 12,
                          padding: '3px 10px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          margin: '0 auto',
                        }}
                      >
                        <span>Open</span>
                        <ArrowUpRight size={13} />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Mobile View All Button */}
        {!!data?.pending.length && (
          <div className="card-mobile-footer">
            <Link to="/projects" className="card-footer-btn">
              <span>View All Projects</span>
              <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </motion.div>

      {/* Recent Client Collections */}
      {!!data?.recentPayments?.length && (
        <motion.div
          className="card"
          style={{ marginTop: 20 }}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15 }}
        >
          <div className="card-head">
            <div className="card-head-title-group">
              <div className="card-head-icon-box card-head-icon-green">
                <TrendingUp size={20} strokeWidth={2.2} />
              </div>
              <div className="card-head-text">
                <h3>Recent Client Collections</h3>
                <div className="desc">Latest verified client payment receipts recorded across projects</div>
              </div>
            </div>
            <Link to="/reports" className="btn btn-auto btn-sm btn-ghost card-head-desktop-link" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', flexShrink: 0 }}>
              <span>Payment Reports</span>
              <ChevronRight size={13} />
            </Link>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Receipt Date</th>
                  <th>Project</th>
                  <th>Client</th>
                  <th>Payment Type</th>
                  <th>Amount Received</th>
                </tr>
              </thead>
              <tbody>
                {data.recentPayments.map((pay) => (
                  <tr key={pay.id}>
                    <td>
                      {new Date(pay.paidOn).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="name">{pay.projectName}</td>
                    <td>{pay.clientName}</td>
                    <td>
                      <span className="chip chip-blue" style={{ textTransform: 'capitalize' }}>
                        {pay.paymentType.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="num" style={{ color: '#059669', fontWeight: 700 }}>
                      +{formatMoney(pay.amountPaise)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Payment Reports Button */}
          <div className="card-mobile-footer">
            <Link to="/reports" className="card-footer-btn">
              <span>View Payment Reports</span>
              <ArrowRight size={14} />
            </Link>
          </div>
        </motion.div>
      )}

      {/* Live Recent Activity */}
      <RecentActivity />
    </Shell>
  );
}

/* ------------------------------- Recent Activity Component ------------------------------- */

function RecentActivity() {
  const { data, isLoading, error, refetch } = useRecentActivity();

  const TONE_STYLES: Record<string, { bg: string; color: string }> = {
    added: { bg: '#eff6ff', color: '#2563eb' },
    approved: { bg: '#ecfdf5', color: '#059669' },
    returned: { bg: '#fef2f2', color: '#ef4444' },
    deleted: { bg: '#fef2f2', color: '#ef4444' },
    restored: { bg: '#ecfdf5', color: '#059669' },
    updated: { bg: '#f1f5f9', color: '#475569' },
  };

  return (
    <motion.div
      className="card"
      style={{ marginTop: 20 }}
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.2 }}
    >
      <div className="card-head">
        <div className="card-head-title-group">
          <div className="card-head-icon-box card-head-icon-slate">
            <Activity size={20} strokeWidth={2.2} />
          </div>
          <div className="card-head-text">
            <h3>Audit Activity Log</h3>
            <div className="desc">Recent ledger entries, site submissions, and approval events</div>
          </div>
        </div>
        <Link to="/activity" className="btn btn-auto btn-sm btn-ghost card-head-desktop-link" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', flexShrink: 0 }}>
          <span>View full log</span>
          <ChevronRight size={13} />
        </Link>
      </div>

      {isLoading ? (
        <div style={{ padding: '8px 0' }}>
          <TableSkeleton rows={4} cols={3} />
        </div>
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data?.length ? (
        <EmptyState
          icon="clipboard"
          title="No recent activity"
          hint="Site entries, bill records, and approvals will appear here live."
        />
      ) : (
        <div>
          {data.map((a) => {
            const toneStyle = TONE_STYLES[a.tone] ?? { bg: '#f1f5f9', color: '#64748b' };
            return (
              <div key={a.id} className="activity-row">
                <div
                  className="activity-icon"
                  style={{
                    background: toneStyle.bg,
                    borderColor: 'transparent',
                    color: toneStyle.color,
                  }}
                >
                  <Icon name={a.icon} />
                </div>
                <div className="activity-content">
                  <div className="activity-text">{a.text}</div>
                  <div className="activity-meta">
                    {a.projectName && <span className="activity-project">{a.projectName}</span>}
                    {a.projectName && <span className="activity-meta-dot">•</span>}
                    <span className="activity-date" title={timeAgo(a.at)}>
                      {exactWhen(a.at)}
                    </span>
                    {a.amountText && (
                      <span className="activity-amount-badge">{a.amountText}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mobile View Full Log Button */}
      {!!data?.length && (
        <div className="card-mobile-footer">
          <Link to="/activity" className="card-footer-btn">
            <span>View Complete Audit Log</span>
            <ArrowRight size={14} />
          </Link>
        </div>
      )}
    </motion.div>
  );
}

/* ------------------------------- Site Staff Dashboard ------------------------------- */

function StaffDashboard() {
  const actor = useSession((s) => s.actor);
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useStaffDashboard(actor?.id);
  const [transferOpen, setTransferOpen] = useState(false);
  const firstName = actor?.fullName?.split(' ')[0] ?? 'Engineer';

  return (
    <Shell
      title="My Site Dashboard"
      crumb="Overview"
      actions={
        Boolean(actor) && (
          <button
            className="btn btn-primary btn-auto btn-sm"
            onClick={() => setTransferOpen(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
          >
            <Repeat size={14} />
            <span>Stock Transfer</span>
          </button>
        )
      }
    >
      {/* Site Engineer Greeting */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          padding: '16px 20px',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          marginBottom: 20,
          boxShadow: 'var(--shadow)',
        }}
      >
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
            Welcome, {firstName} 👷
          </h2>
          <div style={{ fontSize: 12.5, color: 'var(--text-faint)', marginTop: 3 }}>
            Select an assigned project site below to log materials, attendance, or expenses.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button
            className="btn btn-secondary btn-auto btn-sm"
            onClick={() => setTransferOpen(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Repeat size={14} />
            <span>Stock Transfer</span>
          </button>
          <Link to="/my-entries" className="btn btn-primary btn-auto btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FileText size={14} />
            <span>My Entries Log</span>
          </Link>
        </div>
      </motion.div>

      {/* Staff KPI Rows */}
      {isLoading ? (
        <KpiSkeleton count={3} />
      ) : (
        <motion.div
          className="kpi-row"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35, staggerChildren: 0.08 }}
        >
          <motion.div className="kpi" whileHover={{ y: -2 }} transition={{ duration: 0.15 }}>
            <div className="lbl">
              <span>My Assigned Sites</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Building2 size={15} strokeWidth={2} />
              </div>
            </div>
            <div className="val">{data?.sites.length ?? 0}</div>
            <div className="sub">construction sites assigned to you</div>
          </motion.div>

          <motion.div className="kpi" whileHover={{ y: -2 }} transition={{ duration: 0.15 }}>
            <div className="lbl">
              <span>Awaiting Approval</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#fffbeb', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Inbox size={15} strokeWidth={2} />
              </div>
            </div>
            <div className="val" style={{ color: (data?.pendingCount ?? 0) > 0 ? '#ea580c' : undefined }}>
              {data?.pendingCount ?? 0}
            </div>
            <div className="sub">submitted entries under office review</div>
          </motion.div>

          <motion.div className="kpi" whileHover={{ y: -2 }} transition={{ duration: 0.15 }}>
            <div className="lbl">
              <span>Approved This Week</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#ecfdf5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircle2 size={15} strokeWidth={2} />
              </div>
            </div>
            <div className="val" style={{ color: '#059669' }}>
              {data?.approvedThisWeek ?? 0}
            </div>
            <div className="sub">entries approved by administrator</div>
          </motion.div>
        </motion.div>
      )}

      {/* Sites Table */}
      <motion.div
        className="card"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.1 }}
      >
        <div className="card-head">
          <div className="card-head-title-group">
            <div className="card-head-icon-box card-head-icon-blue">
              <Briefcase size={20} strokeWidth={2.2} />
            </div>
            <div className="card-head-text">
              <h3>My Assigned Construction Sites</h3>
              <div className="desc">Click on any site to add bills, daily labour counts, or expense vouchers</div>
            </div>
          </div>
        </div>

        <div className="table-wrap">
          {isLoading ? (
            <TableSkeleton rows={3} cols={4} />
          ) : error ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : !data?.sites.length ? (
            <EmptyState
              icon="briefcase"
              title="No sites assigned yet"
              hint="Please ask your administrator to assign your profile to project sites."
            />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Site Name</th>
                  <th>Location</th>
                  <th>Project Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.sites.map((s) => (
                  <tr
                    key={s.id}
                    className="clickable"
                    onClick={() => navigate(`/projects/${s.id}`)}
                  >
                    <td className="name">
                      <div style={{ fontWeight: 600, color: 'var(--text)' }}>{s.name}</div>
                    </td>
                    <td>
                      <span style={{ color: 'var(--text-soft)' }}>{s.location ?? '—'}</span>
                    </td>
                    <td>
                      <StatusChip value={s.status} />
                    </td>
                    <td>
                      <span
                        className="btn btn-auto btn-sm btn-ghost"
                        style={{
                          fontSize: 12,
                          padding: '3px 10px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          margin: '0 auto',
                        }}
                      >
                        <span>Open Site</span>
                        <ArrowUpRight size={13} />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Mobile My Entries Button */}
        <div className="card-mobile-footer">
          <Link to="/my-entries" className="card-footer-btn">
            <span>View My Entries Log</span>
            <ArrowRight size={14} />
          </Link>
        </div>
      </motion.div>

      {transferOpen && <StockTransferDrawer onClose={() => setTransferOpen(false)} />}
    </Shell>
  );
}
